import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Phone, Clock, Mail, Send, Loader2, Navigation, MessageCircle } from "lucide-react";

const services = [
    "Schuhreparatur", "Absätze & Sohlen", "Schlüsselkopie", "Autoschlüssel",
    "Gravur", "Stempel", "Namensschild", "Sonstiges",
];

const hours = [
    { day: "Montag", time: "10:00 – 20:00" },
    { day: "Dienstag", time: "10:00 – 20:00" },
    { day: "Mittwoch", time: "10:00 – 20:00" },
    { day: "Donnerstag", time: "10:00 – 20:00" },
    { day: "Freitag", time: "10:00 – 20:00" },
    { day: "Samstag", time: "10:00 – 20:00" },
    { day: "Sonntag", time: "Geschlossen" },
];

export default function Kontakt() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const validate = () => {
        const e = {};
        if (!form.name || form.name.trim().length < 2) e.name = "Bitte Namen angeben";
        if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Bitte gültige E-Mail angeben";
        if (!form.message || form.message.trim().length < 5) e.message = "Bitte eine Nachricht schreiben";
        return e;
    };

    const handleChange = (field) => (ev) => {
        setForm((f) => ({ ...f, [field]: ev.target.value }));
        if (errors[field]) setErrors((er) => ({ ...er, [field]: undefined }));
    };

    const handleSubmit = async (ev) => {
        ev.preventDefault();
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); toast.error("Bitte Formular prüfen"); return; }
        setLoading(true);
        try {
            const subject = encodeURIComponent(`Anfrage Mein Schuster${form.service ? " – " + form.service : ""}`);
            const bodyLines = [
                `Name: ${form.name.trim()}`,
                `E-Mail: ${form.email.trim()}`,
                form.phone ? `Telefon: ${form.phone.trim()}` : null,
                form.service ? `Leistung: ${form.service}` : null,
                "",
                "Nachricht:",
                form.message.trim(),
            ].filter(Boolean);
            const body = encodeURIComponent(bodyLines.join("\n"));
            window.location.href = `mailto:agisschluesseldienst@gmail.com?subject=${subject}&body=${body}`;
            toast.success("E-Mail-Programm geöffnet – bitte Nachricht absenden.");
            setForm({ name: "", email: "", phone: "", service: "", message: "" });
        } catch (err) {
            toast.error("Bitte versuchen Sie es per Telefon oder WhatsApp.");
            console.error(err);
        } finally { setLoading(false); }
    };

    const inputCls = (has) =>
        `w-full bg-white border ${has ? "border-red-500" : "border-line focus:border-ink"} py-3 px-4 font-body text-base text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-brand/20 transition-colors rounded-sm`;

    return (
        <div data-testid="page-kontakt">
            {/* Header */}
            <section className="pt-16 md:pt-24 pb-10 border-b border-line">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                    <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] font-medium text-ink max-w-4xl">
                        So <span className="italic text-brand">erreichen</span> Sie uns!
                    </h1>
                </div>
            </section>

            {/* Quick-action bar */}
            <section className="bg-white border-b border-line" data-testid="contact-quick-actions">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 py-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <a href="tel:+499414610027" data-testid="quick-action-call"
                       className="group flex items-center justify-between bg-brand text-paper px-5 py-4 hover:bg-brandDark transition-colors">
                        <span className="flex items-center gap-3">
                            <Phone size={18} /><span className="font-body text-sm tracking-[0.15em] uppercase font-semibold">Anrufen</span>
                        </span>
                        <span className="font-body text-xs text-paper/70 tabular-nums">0941 461 0027</span>
                    </a>
                    <a href="https://wa.me/4917620366268" target="_blank" rel="noopener noreferrer" data-testid="quick-action-whatsapp"
                       className="group flex items-center justify-between bg-[#25D366] text-white px-5 py-4 hover:bg-[#1DA851] transition-colors">
                        <span className="flex items-center gap-3">
                            <MessageCircle size={18} /><span className="font-body text-sm tracking-[0.15em] uppercase font-semibold">WhatsApp</span>
                        </span>
                        <span className="font-body text-xs text-white/80 truncate ml-2">schnell antworten</span>
                    </a>
                    <a href="mailto:agisschluesseldienst@gmail.com" data-testid="quick-action-email"
                       className="group flex items-center justify-between bg-ink text-paper px-5 py-4 hover:bg-inkSoft transition-colors">
                        <span className="flex items-center gap-3">
                            <Mail size={18} /><span className="font-body text-sm tracking-[0.15em] uppercase font-semibold">E-Mail</span>
                        </span>
                        <span className="font-body text-xs text-paper/70 truncate ml-2">an uns schreiben</span>
                    </a>
                    <a href="https://www.google.com/maps/search/?api=1&query=Weichser+Weg+5+93059+Regensburg" target="_blank" rel="noopener noreferrer" data-testid="quick-action-directions"
                       className="group flex items-center justify-between bg-white border border-ink text-ink px-5 py-4 hover:bg-ink hover:text-paper transition-colors">
                        <span className="flex items-center gap-3">
                            <Navigation size={18} /><span className="font-body text-sm tracking-[0.15em] uppercase font-semibold">Route</span>
                        </span>
                        <span className="font-body text-xs opacity-70">zum DEZ</span>
                    </a>
                </div>
            </section>

            {/* Main grid — Form full width, info cards below */}
            <section className="py-20 md:py-24">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
                    {/* Form card */}
                    <div className="bg-white border border-line p-8 md:p-10 mb-16">
                        <h3 className="font-display text-xl font-bold text-ink pb-3 border-b-2 border-ink inline-block mb-2">Anfrage</h3>
                        <h2 className="font-display text-3xl md:text-4xl tracking-tight text-ink mt-4 mb-8">
                            Nachricht senden
                        </h2>

                        <form onSubmit={handleSubmit} data-testid="contact-form" noValidate>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                <div>
                                    <label className="block mb-2 text-xs font-bold uppercase tracking-wider text-ink">Name *</label>
                                    <input type="text" value={form.name} onChange={handleChange("name")} placeholder="Ihr Name" data-testid="contact-input-name" className={inputCls(errors.name)} />
                                    {errors.name && <p className="mt-1.5 text-xs text-red-600 font-body">{errors.name}</p>}
                                </div>
                                <div>
                                    <label className="block mb-2 text-xs font-bold uppercase tracking-wider text-ink">E-Mail *</label>
                                    <input type="email" value={form.email} onChange={handleChange("email")} placeholder="name@beispiel.de" data-testid="contact-input-email" className={inputCls(errors.email)} />
                                    {errors.email && <p className="mt-1.5 text-xs text-red-600 font-body">{errors.email}</p>}
                                </div>
                                <div>
                                    <label className="block mb-2 text-xs font-bold uppercase tracking-wider text-ink">Telefon</label>
                                    <input type="tel" value={form.phone} onChange={handleChange("phone")} placeholder="Optional" data-testid="contact-input-phone" className={inputCls(false)} />
                                </div>
                                <div>
                                    <label className="block mb-2 text-xs font-bold uppercase tracking-wider text-ink">Leistung</label>
                                    <select value={form.service} onChange={handleChange("service")} data-testid="contact-select-service" className={`${inputCls(false)} appearance-none cursor-pointer`}>
                                        <option value="">Auswählen</option>
                                        {services.map((s) => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block mb-2 text-xs font-bold uppercase tracking-wider text-ink">Nachricht *</label>
                                    <textarea value={form.message} onChange={handleChange("message")} rows={5} placeholder="Beschreiben Sie kurz Ihr Anliegen…" data-testid="contact-input-message" className={`${inputCls(errors.message)} resize-none`} />
                                    {errors.message && <p className="mt-1.5 text-xs text-red-600 font-body">{errors.message}</p>}
                                </div>
                            </div>

                            <div className="mt-8 pt-6 border-t border-line flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
                                <p className="font-body text-xs text-inkSoft max-w-sm leading-relaxed">
                                    Mit dem Absenden stimmen Sie der Verarbeitung Ihrer Daten zur
                                    Bearbeitung der Anfrage zu.
                                </p>
                                <button type="submit" disabled={loading} data-testid="contact-submit-button"
                                        className="group inline-flex items-center gap-3 bg-brand text-paper px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-brandDark transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
                                    {loading ? (<><Loader2 size={16} className="animate-spin" /> Senden …</>)
                                             : (<>Anfrage senden <Send size={14} className="group-hover:translate-x-0.5 transition-transform" /></>)}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Info row — 3 cards side by side */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="contact-info-panel">
                        {/* Direkt erreichbar */}
                        <div className="bg-white border border-line p-8 flex flex-col">
                            <h3 className="font-display text-xl font-bold text-ink pb-3 border-b-2 border-ink inline-block self-start mb-6">Direkt erreichbar</h3>
                            <ul className="space-y-5 font-body text-sm flex-1">
                                <li className="flex gap-4 items-start">
                                    <MapPin size={18} className="mt-0.5 text-brand shrink-0" />
                                    <span className="text-ink">
                                        Weichser Weg 5<br/>93059 Regensburg<br/>
                                        <span className="text-inkSoft">Donau-Einkaufszentrum (DEZ)</span>
                                    </span>
                                </li>
                                <li className="flex gap-4 items-start">
                                    <Phone size={18} className="mt-0.5 text-brand shrink-0" />
                                    <a href="tel:+499414610027" className="text-ink hover:text-brand transition-colors font-semibold" data-testid="contact-phone-link">+49 941 4610027</a>
                                </li>
                                <li className="flex gap-4 items-start">
                                    <Mail size={18} className="mt-0.5 text-brand shrink-0" />
                                    <a href="mailto:agisschluesseldienst@gmail.com" className="text-ink hover:text-brand transition-colors break-all" data-testid="contact-email-link">agisschluesseldienst@gmail.com</a>
                                </li>
                            </ul>

                            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <a href="tel:+499414610027" data-testid="contact-extra-call-button"
                                   className="group flex items-center justify-center gap-2 bg-brand text-paper px-4 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-brandDark transition-colors">
                                    <Phone size={14} />
                                    Anrufen
                                </a>
                                <a href="https://wa.me/4917620366268" target="_blank" rel="noopener noreferrer" data-testid="contact-extra-whatsapp-button"
                                   className="group flex items-center justify-center gap-2 bg-[#25D366] text-white px-4 py-4 text-xs tracking-[0.2em] uppercase font-semibold hover:bg-[#1DA851] transition-colors">
                                    <MessageCircle size={14} />
                                    WhatsApp
                                </a>
                            </div>
                        </div>

                        {/* Öffnungszeiten */}
                        <div className="bg-ink text-paper p-8 flex flex-col">
                            <h3 className="font-display text-xl font-bold text-paper pb-3 border-b-2 border-paper inline-block self-start mb-6 flex items-center gap-2">
                                <Clock size={16} /> Öffnungszeiten
                            </h3>
                            <ul className="space-y-3 font-body text-sm flex-1">
                                {hours.map((h) => (
                                    <li key={h.day} className="flex items-baseline justify-between gap-4 border-b border-paper/10 pb-2 last:border-0">
                                        <span className="text-paper/80">{h.day}</span>
                                        <span className={`tabular-nums font-semibold ${h.time === "Geschlossen" ? "text-brand/70" : "text-paper"}`}>{h.time}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Standort / Map */}
                        <div className="bg-white border border-line p-8 flex flex-col">
                            <h3 className="font-display text-xl font-bold text-ink pb-3 border-b-2 border-ink inline-block self-start mb-6">Standort – DEZ, Ebene 2</h3>
                            <div className="aspect-[4/3] overflow-hidden border border-line flex-1" data-testid="contact-map-wrap">
                                <iframe
                                    title="Standort Regensburg"
                                    src="https://www.google.com/maps?q=Weichser+Weg+5,+93059+Regensburg&output=embed"
                                    width="100%" height="100%" style={{ border: 0 }}
                                    loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
